import React from "react";
import { Box, Stack, Typography, CircularProgress } from "@mui/material";
import Pagination from "@mui/material/Pagination";

/**
 * ページング付きの一覧レイアウト
 *
 * props:
 * - items: 表示対象の配列
 * - loading: 読み込み中フラグ
 * - pagination: { current_page, total_pages, total_count, per_page }
 * - onPageChange: (page:number) => void
 * - emptyText: 0件のときの文言
 * - children: リスト本体
 */
export default function PaginatedListLayout({
  items = [],
  loading = false,
  pagination,
  onPageChange,
  emptyText = "該当するデータがありません。",
  children,
}) {
  // APIから返るページ情報
  const rawCurrentPage = pagination?.current_page ?? 1;
  const totalPages = Math.max(pagination?.total_pages ?? 1, 1);
  const totalCount = pagination?.total_count;
  const perPage = pagination?.per_page ?? items.length;

  // Paginationの仕様に合わせてページ番号を1〜totalPagesにクランプ
  const currentPage = Math.min(Math.max(rawCurrentPage, 1), totalPages);

  // 表示件数
  const startCount = items.length > 0 ? (currentPage - 1) * perPage + 1 : 0;
  const endCount =
    items.length > 0
      ? Math.min(
          (currentPage - 1) * perPage + items.length,
          totalCount ?? items.length,
        )
      : 0;

  const handleChange = (_, value) => {
    if (loading) return;
    if (!onPageChange) return;
    if (value === currentPage) return;

    onPageChange(value);
  };

  return (
    <Box>
      {/* 件数表示 */}
      {!loading && totalCount > 0 && (
        <Stack alignItems="flex-start" sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            <Box component="span" sx={{ fontSize: 16, fontWeight: 600 }}>
              {startCount}〜{endCount}
            </Box>
            <Box component="span" sx={{ fontSize: 12 }}>
              件を表示 / 全
            </Box>
            <Box
              component="span"
              sx={{ fontSize: 16, fontWeight: 600, ml: 0.5 }}
            >
              {totalCount}
            </Box>
            <Box component="span" sx={{ fontSize: 12, ml: 0.5 }}>
              件
            </Box>
          </Typography>
        </Stack>
      )}

      {/* 本体 */}
      {loading ? (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress />
        </Stack>
      ) : items.length === 0 ? (
        <Typography
          sx={{ py: 6, whiteSpace: "pre-line" }}
          color="text.secondary"
        >
          {emptyText}
        </Typography>
      ) : (
        <Box>{children}</Box>
      )}

      {/* ページング */}
      {totalPages > 1 && (
        <Stack alignItems="center" sx={{ mt: 3 }}>
          <Pagination
            page={currentPage}
            count={totalPages}
            onChange={handleChange}
            disabled={loading}
          />
        </Stack>
      )}
    </Box>
  );
}
